import type { IModeratedNeighborhood, IUser, NeighborhoodModeratorRight } from './types';

// ==============================|| MODERATOR DE CARTIER ||============================== //
// Un cetățean promovat moderator intră în admin doar pentru cartierul lui. BE-ul îi deschide
// endpoint-urile de conținut prin aceleași chei de permisiune pe care le adăugăm aici, deci
// gate-urile existente (PermissionGate, canWrite din tab-uri) funcționează nemodificate; fiecare
// apel e oricum limitat de BE la cartierul lui.

/** Cheile de permisiune deschise de fiecare drept (oglinda BE: AdminPermissionKeys). */
const PERMISSION_KEYS_BY_RIGHT: Record<NeighborhoodModeratorRight, string[]> = {
	announcements: ['announcements:read', 'announcements:write', 'announcements:delete'],
	surveys: ['surveys:read', 'surveys:write', 'surveys:delete', 'surveys:export'],
	chat: []
};

const RIGHTS = Object.keys(PERMISSION_KEYS_BY_RIGHT) as NeighborhoodModeratorRight[];

/** Normalizează cartierul moderat din api/users/me; null dacă lipsește sau nu are niciun drept. */
export const readModeratedNeighborhood = (raw: unknown): IModeratedNeighborhood | null => {
	const value = (raw as { moderatedNeighborhood?: Partial<IModeratedNeighborhood> | null })
		?.moderatedNeighborhood;
	if (!value || typeof value.id !== 'number') return null;
	const permissions = (Array.isArray(value.permissions) ? value.permissions : []).filter(
		(right): right is NeighborhoodModeratorRight => RIGHTS.includes(right)
	);
	if (permissions.length === 0) return null;
	return {
		id: value.id,
		name: value.name ?? '',
		shortName: value.shortName ?? '',
		isArchived: Boolean(value.isArchived),
		permissions
	};
};

export const moderatorPermissionKeys = (neighborhood: IModeratedNeighborhood): string[] =>
	neighborhood.permissions.flatMap((right) => PERMISSION_KEYS_BY_RIGHT[right]);

type ModerationUser =
	| { role?: IUser['role'] | string; moderatedNeighborhood?: IUser['moderatedNeighborhood'] }
	| null
	| undefined;

export const isNeighborhoodModerator = (user: ModerationUser): boolean =>
	user?.role === 'neighborhoodModerator' && Boolean(user.moderatedNeighborhood);

export const hasModeratorRight = (
	user: ModerationUser,
	right: NeighborhoodModeratorRight
): boolean =>
	isNeighborhoodModerator(user) &&
	Boolean(user?.moderatedNeighborhood?.permissions.includes(right));

/** Pagina de start a moderatorului: pagina cartierului lui. */
export const moderatorHomePath = (user: ModerationUser): string =>
	`/common/cartiere/${user?.moderatedNeighborhood?.id ?? ''}`;

const CONTENT_ROUTES: { pattern: RegExp; right: NeighborhoodModeratorRight }[] = [
	{ pattern: /^\/common\/announcment\/(create|edit\/\d+|\d+)$/, right: 'announcements' },
	{ pattern: /^\/common\/consultation\/(create|edit\/\d+|\d+)$/, right: 'surveys' }
];

/**
 * Rutele pe care un moderator le poate deschide: pagina cartierului lui, editoarele și detaliile
 * de conținut pentru care are dreptul și profilul. Orice altceva îl întoarce acasă.
 */
export const isModeratorPathAllowed = (user: ModerationUser, pathname: string): boolean => {
	if (!isNeighborhoodModerator(user)) return false;
	const path = pathname.replace(/\/+$/, '');
	if (path === moderatorHomePath(user) || path === '/common/profile') return true;
	return CONTENT_ROUTES.some(
		({ pattern, right }) => pattern.test(path) && hasModeratorRight(user, right)
	);
};
